/**
 * Helpers for the `pending_invite_*` localStorage keys used by the client
 * signup flow to bridge invite context across the email-confirmation round trip.
 *
 * Why this exists: before this module, writes and deletes of these keys were
 * scattered across `Signup.tsx` and `AuthCallback.tsx`. Several exit branches
 * (OTP expired, email-link errors, invite redeem failures, user aborts) never
 * cleared the keys, so a stale invite could bleed into the next user's signup
 * on the same browser. There was also no TTL, so keys persisted indefinitely.
 *
 * This module fixes both problems:
 * - `savePendingInvite` stamps a timestamp alongside the keys.
 * - `readPendingInvite` returns `null` and auto-clears when the TTL elapses.
 * - `clearPendingInvite` wipes all four keys; call it from every exit branch
 *   and from the global sign-out path.
 */

const ID_KEY = 'pending_invite_id';
const TOKEN_KEY = 'pending_invite_token';
const EMAIL_KEY = 'pending_invite_email';
const TIMESTAMP_KEY = 'pending_invite_saved_at';

/**
 * Invites live in this jar for at most 24 hours. That comfortably covers the
 * Supabase magic-link / confirm-email window without letting truly stale
 * invites bleed into unrelated signups.
 */
const PENDING_INVITE_TTL_MS = 24 * 60 * 60 * 1000;

export interface PendingInvite {
  inviteId?: string;
  token?: string;
  email?: string;
}

export function savePendingInvite(invite: PendingInvite): void {
  try {
    if (invite.inviteId) localStorage.setItem(ID_KEY, invite.inviteId);
    if (invite.token) localStorage.setItem(TOKEN_KEY, invite.token);
    if (invite.email) localStorage.setItem(EMAIL_KEY, invite.email);
    localStorage.setItem(TIMESTAMP_KEY, String(Date.now()));
  } catch {
    /* ignored — quota / private mode */
  }
}

export function readPendingInvite(): PendingInvite {
  let inviteId: string | null = null;
  let token: string | null = null;
  let email: string | null = null;
  let savedAtRaw: string | null = null;
  try {
    inviteId = localStorage.getItem(ID_KEY);
    token = localStorage.getItem(TOKEN_KEY);
    email = localStorage.getItem(EMAIL_KEY);
    savedAtRaw = localStorage.getItem(TIMESTAMP_KEY);
  } catch {
    return {};
  }

  // No stamp on legacy rows or tampered storage → treat as expired. Clear so
  // we don't keep reading stale data every load.
  if (!savedAtRaw) {
    if (inviteId || token || email) clearPendingInvite();
    return {};
  }

  const savedAt = Number(savedAtRaw);
  if (!Number.isFinite(savedAt) || Date.now() - savedAt > PENDING_INVITE_TTL_MS) {
    clearPendingInvite();
    return {};
  }

  return {
    inviteId: inviteId || undefined,
    token: token || undefined,
    email: email || undefined,
  };
}

export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(ID_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(TIMESTAMP_KEY);
  } catch {
    // OBS-11 a-ignore: localStorage throws in private mode / when full; this is a best-effort cache and the app works without it.
  }
}
